import { useState } from 'react';
import { Link } from 'react-router';
import { Ellipsis } from 'lucide-react';
import type { BoardColumn } from '@rundown/types';
import { Popover } from '../../components';
import { cx } from '../../lib/cx';
import { useSetColumn } from '../board/useSetColumn';
import { BOARD_COLUMNS, COLUMN_LABELS } from './labels';

/**
 * The per-request menu: open it, or move it to a column.
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 * Status could be changed in exactly two places — by dragging a card on Plan ›
 * Delivery, or by opening the request. Neither is where the question usually
 * comes up: you are looking at a list, you know this one is finished, and the
 * cheapest way to say so was to navigate away from what you were reading. A
 * request is listed on five screens and could be moved on one of them.
 *
 * ── Why a menu and not a select ─────────────────────────────────────────────
 * A `<select>` on every row is six columns of chrome on a screen whose job is
 * to be read. The trigger is one glyph until it is wanted, and the panel is
 * portalled out of the row, so neither the drag listeners nor the row's own
 * link have to know about it.
 *
 * ── Why it survives being inside a draggable ────────────────────────────────
 * Every list this appears on activates its pointer sensor past 4–5px of
 * movement, which is what already lets a card's title be a link. A press that
 * does not move is a click; the menu needs no `stopPropagation` and the drag
 * needs no exception.
 *
 * Render it only for a caller holding `board.write` — the move it offers is
 * refused without it, and a menu whose only item is "Open" duplicates the
 * title beside it.
 */

export interface RequestMenuProps {
  readonly workspaceId: string;
  readonly requestId: string;
  readonly number: number;
  /** The column it is in now, shown as the checked item. */
  readonly column: BoardColumn;
  readonly className?: string;
}

export function RequestMenu({ workspaceId, requestId, number, column, className }: RequestMenuProps) {
  const [open, setOpen] = useState(false);
  const setColumn = useSetColumn(workspaceId);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label={`Actions for #${number}`}
      className="w-52"
      trigger={
        <button
          type="button"
          aria-label={`Actions for #${number}`}
          // The row's drag listeners sit on an ancestor and fire on pointer
          // movement, not on this click — see the header. `touch-none` keeps a
          // tap on a phone from being read as the start of a scroll.
          className={cx(
            'grid size-6 flex-none touch-none place-items-center rounded-md text-muted',
            'hover:bg-surface-2 hover:text-ink aria-expanded:bg-surface-2 aria-expanded:text-ink',
            className,
          )}
        >
          <Ellipsis size={15} aria-hidden />
        </button>
      }
    >
      <div role="menu">
        <Link
          to={`/requests/${requestId}`}
          role="menuitem"
          onClick={() => setOpen(false)}
          className="block border-b border-surface-3 px-3.5 py-2.5 fs-12.5 text-ink hover:bg-surface"
        >
          Open request
        </Link>

        <div className="px-3.5 pt-2.5 pb-1 fs-10.5 font-bold tracking-[0.06em] text-muted uppercase">
          Move to
        </div>
        {BOARD_COLUMNS.map((option) => (
          <button
            key={option}
            type="button"
            role="menuitemradio"
            aria-checked={option === column}
            disabled={setColumn.isPending}
            onClick={() => {
              // Already there: close, write nothing. The server would no-op on
              // the column but still compute a fresh rank for it, which would
              // silently move the card to the top of its own column.
              if (option === column) {
                setOpen(false);
                return;
              }
              setColumn.mutate({ requestId, column: option }, { onSuccess: () => setOpen(false) });
            }}
            className={cx(
              'flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left fs-12.5',
              'hover:bg-surface disabled:opacity-50',
              option === column ? 'font-semibold text-ink' : 'text-ink-soft',
            )}
          >
            {COLUMN_LABELS[option]}
            {option === column && <span aria-hidden>✓</span>}
          </button>
        ))}

        {setColumn.error !== null && (
          <p role="alert" className="border-t border-surface-3 px-3.5 py-2 fs-11 text-danger">
            {setColumn.error.message}
          </p>
        )}
      </div>
    </Popover>
  );
}
